import { Check, ChevronDown, ChevronRight, Circle, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ProcessItem, ProcessItemStatus } from "../../api/types.js";

type DisplayProcessItem = ProcessItem & { legacyDetail?: string };

function formatDuration(ms?: number) {
  const seconds = Math.max(0, Math.round((ms || 0) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function pocketLabel(status: ProcessItemStatus, elapsedMs?: number) {
  const duration = formatDuration(elapsedMs);
  if (status === "running") return `正在处理 ${duration}`;
  if (status === "waiting") return `等待确认 ${duration}`;
  if (status === "cancelled") return `你在 ${duration} 后停止了`;
  if (status === "failed") return `处理失败 ${duration}`;
  return `已处理 ${duration}`;
}

function normalizeStatus(status?: string): ProcessItemStatus {
  if (status === "active" || status === "running") return "running";
  if (status === "waiting") return "waiting";
  if (status === "error" || status === "failed") return "failed";
  if (status === "cancelled") return "cancelled";
  return "completed";
}

function normalizeItem(item: any, index: number): DisplayProcessItem {
  if (item?.itemId) return item as DisplayProcessItem;
  const status = normalizeStatus(item?.status);
  const elapsedMs = Number(item?.elapsedMs || 0);
  const detail = typeof item?.detail === "string" ? item.detail : item?.text || "";
  return {
    itemId: String(item?.id || `legacy-process-${index}`),
    kind: item?.kind === "tool" ? "tool" : item?.kind === "reasoning" ? "phase" : "phase",
    label: String(item?.label || (item?.kind === "reasoning" ? "思考过程" : "处理过程")),
    ...(detail ? { detail: { text: detail }, legacyDetail: detail } : {}),
    status,
    startedElapsedMs: 0,
    ...(status === "running" || status === "waiting" ? {} : {
      completedElapsedMs: elapsedMs,
      durationMs: elapsedMs
    })
  };
}

function detailLines(item: DisplayProcessItem) {
  const detail = item.detail || {};
  const lines: string[] = [];
  if (detail.inputSummary) lines.push(`输入：${detail.inputSummary}`);
  if (detail.outputSummary) lines.push(`结果：${detail.outputSummary}`);
  if (Number.isFinite(detail.resultCount)) lines.push(`结果数量：${detail.resultCount}`);
  if (detail.warning) lines.push(`提示：${detail.warning}`);
  if (detail.compaction) {
    const compaction = detail.compaction;
    const reasonMap: Record<string, string> = { manual: "手动", threshold: "达到上下文阈值", overflow: "上下文溢出" };
    lines.push(`触发原因：${reasonMap[compaction.reason] || compaction.reason}`);
    if (Number.isFinite(compaction.tokensBefore)) lines.push(`压缩前：${Number(compaction.tokensBefore).toLocaleString()} tokens`);
    if (Number.isFinite(compaction.estimatedTokensAfter)) lines.push(`压缩后估算：${Number(compaction.estimatedTokensAfter).toLocaleString()} tokens`);
    if (compaction.willRetry) lines.push("压缩完成后将自动重试当前请求");
    if (compaction.aborted) lines.push("压缩已取消");
    if (compaction.error) lines.push(`错误：${compaction.error}`);
  }
  if (detail.text && item.kind !== "commentary") lines.push(String(detail.text));
  if (item.durationMs !== undefined) lines.push(`耗时：${formatDuration(item.durationMs)}`);
  return lines;
}

function itemStatusLabel(item: DisplayProcessItem, displayedElapsedMs: number) {
  if (item.status === "running") return formatDuration(Math.max(0, displayedElapsedMs - item.startedElapsedMs));
  if (item.status === "waiting") return "等待确认";
  if (item.status === "failed") return "未完成";
  if (item.status === "cancelled") return "已停止";
  return "";
}

function ProcessStatusIcon({ status }: { status: ProcessItemStatus }) {
  if (status === "completed") return <Check size={13} aria-hidden="true" />;
  if (status === "failed" || status === "cancelled") return <XCircle size={13} aria-hidden="true" />;
  return <Circle size={9} fill="currentColor" aria-hidden="true" />;
}

function ProcessRow({
  item,
  depth,
  displayedElapsedMs,
  children
}: {
  item: DisplayProcessItem;
  depth: number;
  displayedElapsedMs: number;
  children?: React.ReactNode;
}) {
  const lines = detailLines(item);
  const statusLabel = itemStatusLabel(item, displayedElapsedMs);
  if (item.kind === "commentary") {
    return (
      <div className="run-trace-commentary" style={{ "--process-depth": depth } as React.CSSProperties}>
        <span>{item.detail?.text || item.summary || item.label}</span>
        {statusLabel && <em>{statusLabel}</em>}
      </div>
    );
  }
  return (
    <div className={`run-trace-item is-${item.status} kind-${item.kind}`} style={{ "--process-depth": depth } as React.CSSProperties}>
      <div className="run-trace-item-row">
        <span className="run-trace-item-icon"><ProcessStatusIcon status={item.status} /></span>
        <span className="run-trace-item-label">{item.label}</span>
        {statusLabel && <em>{statusLabel}</em>}
      </div>
      {lines.length > 0 && (
        <details className="activity-detail">
          <summary>查看详情</summary>
          <pre>{lines.join("\n")}</pre>
        </details>
      )}
      {children}
    </div>
  );
}

function useDisplayedElapsed(status: ProcessItemStatus, elapsedMs?: number) {
  const [now, setNow] = useState(() => Date.now());
  const baseline = useRef({ elapsedMs: elapsedMs || 0, at: Date.now() });

  useEffect(() => {
    baseline.current = { elapsedMs: elapsedMs || 0, at: Date.now() };
    setNow(Date.now());
  }, [elapsedMs, status]);

  useEffect(() => {
    if (status !== "running") return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  if (status !== "running") return elapsedMs || 0;
  return Math.max(elapsedMs || 0, baseline.current.elapsedMs + now - baseline.current.at);
}

export function ProcessPocket({
  items,
  status,
  elapsedMs,
  expanded,
  onToggle
}: {
  items: any[];
  status: ProcessItemStatus;
  elapsedMs?: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const displayedElapsedMs = useDisplayedElapsed(status, elapsedMs);
  const normalizedItems = useMemo(() => items.map(normalizeItem), [items]);
  const childrenByParent = useMemo(() => {
    const map = new Map<string, DisplayProcessItem[]>();
    for (const item of normalizedItems) {
      if (!item.parentItemId) continue;
      const children = map.get(item.parentItemId) || [];
      children.push(item);
      map.set(item.parentItemId, children);
    }
    return map;
  }, [normalizedItems]);
  const roots = normalizedItems.filter((item) => !item.parentItemId || !normalizedItems.some((candidate) => candidate.itemId === item.parentItemId));
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelFollowPausedRef = useRef(false);
  const lastPanelScrollTopRef = useRef(0);
  const panelFollowFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!expanded || status !== "running") {
      panelFollowPausedRef.current = false;
      return undefined;
    }
    const panel = panelRef.current;
    if (!panel) return undefined;
    const nearBottom = () => panel.scrollHeight - panel.clientHeight - panel.scrollTop <= 36;
    const handleScroll = () => {
      const nextTop = panel.scrollTop;
      if (nextTop < lastPanelScrollTopRef.current - 2 && !nearBottom()) panelFollowPausedRef.current = true;
      if (nearBottom()) panelFollowPausedRef.current = false;
      lastPanelScrollTopRef.current = nextTop;
    };
    lastPanelScrollTopRef.current = panel.scrollTop;
    panel.addEventListener("scroll", handleScroll, { passive: true });
    return () => panel.removeEventListener("scroll", handleScroll);
  }, [expanded, status]);

  useEffect(() => {
    if (!expanded || status !== "running" || panelFollowPausedRef.current) return undefined;
    if (panelFollowFrameRef.current) window.cancelAnimationFrame(panelFollowFrameRef.current);
    let remainingFrames = 3;
    const follow = () => {
      panelFollowFrameRef.current = window.requestAnimationFrame(() => {
        panelFollowFrameRef.current = null;
        const panel = panelRef.current;
        if (!panel || panelFollowPausedRef.current) return;
        panel.scrollTo({ top: panel.scrollHeight, behavior: "auto" });
        lastPanelScrollTopRef.current = panel.scrollTop;
        if (remainingFrames > 0) {
          remainingFrames -= 1;
          follow();
        }
      });
    };
    follow();
    return () => {
      if (panelFollowFrameRef.current) window.cancelAnimationFrame(panelFollowFrameRef.current);
      panelFollowFrameRef.current = null;
    };
  }, [expanded, normalizedItems, status]);

  const renderItem = (item: DisplayProcessItem, depth = 0): React.ReactNode => (
    <ProcessRow key={item.itemId} item={item} depth={depth} displayedElapsedMs={displayedElapsedMs}>
      {(childrenByParent.get(item.itemId) || []).map((child) => renderItem(child, depth + 1))}
    </ProcessRow>
  );

  if (!items.length && status === "completed") return null;
  return (
    <div className={`assistant-process is-${status}`} data-inline-activity-root>
      <button className="assistant-process-toggle" type="button" aria-expanded={expanded} onClick={onToggle}>
        <span className="assistant-process-main">
          <span className="assistant-process-dot" aria-hidden="true" />
          <span data-inline-activity-label>{pocketLabel(status, displayedElapsedMs)}</span>
        </span>
        <span className="assistant-process-meta" aria-hidden="true">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>
      {expanded && (
        <div className="assistant-process-panel" ref={panelRef}>
          {roots.map((item) => renderItem(item))}
          {roots.length === 0 && <p className="empty-note">正在准备本轮处理。</p>}
        </div>
      )}
    </div>
  );
}

export function ActivityProcess({
  events,
  live,
  expanded,
  onToggle
}: {
  events: any[];
  live?: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return <ProcessPocket items={events} status={live ? "running" : "completed"} elapsedMs={events.at(-1)?.elapsedMs} expanded={expanded} onToggle={onToggle} />;
}
